import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  Clock, CheckCircle, XCircle, Briefcase, MapPin, Building2, 
  User, Calendar, Loader2, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const OffresEnAttenteAdmin = () => {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRefusDialog, setShowRefusDialog] = useState(false);
  const [motifRefus, setMotifRefus] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadOffres();
  }, []);

  const loadOffres = async () => {
    try {
      const response = await api.get('/offres-emploi/en-attente');
      setOffres(response.data || []);
    } catch (error) {
      console.error('Erreur chargement offres en attente:', error);
      toast.error('Erreur de chargement des offres en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (offreId) => {
    setProcessing(true);
    try {
      await api.put(`/offres-emploi/${offreId}/valider`);
      toast.success('Offre validée et publiée !');
      loadOffres();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setProcessing(false);
    }
  };

  const handleRefuser = async () => {
    if (!selectedOffre) return;
    
    setProcessing(true);
    try {
      await api.put(`/offres-emploi/${selectedOffre.id}/refuser`, { motif: motifRefus });
      toast.success('Offre refusée');
      loadOffres();
      setShowRefusDialog(false);
      setShowDetailDialog(false);
      setMotifRefus('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setProcessing(false);
    }
  };

  const openRefusDialog = (offre) => {
    setSelectedOffre(offre);
    setMotifRefus('');
    setShowRefusDialog(true);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'financeur':
        return <Badge className="bg-purple-100 text-purple-700">Financeur</Badge>;
      case 'referent':
        return <Badge className="bg-blue-100 text-blue-700">Référent</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{role}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-3xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2 text-amber-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <Clock className="w-6 h-6" />
            Offres en Attente de Validation
            {offres.length > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white">{offres.length}</Badge>
            )}
          </CardTitle>
          <p className="text-amber-700 text-sm">
            Les offres créées par les Financeurs et Référents nécessitent votre validation avant publication.
          </p>
        </CardHeader>
      </Card>

      {/* Liste des offres */}
      {offres.length === 0 ? (
        <Card className="bg-white rounded-3xl border-slate-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
            <p className="text-slate-600 font-medium">Aucune offre en attente</p>
            <p className="text-sm text-slate-400 mt-1">Toutes les offres ont été traitées</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {offres.map((offre) => (
            <Card 
              key={offre.id} 
              className="bg-white rounded-3xl border-amber-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Badges */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className="bg-amber-100 text-amber-700">
                        <Clock className="w-3 h-3 mr-1" />
                        En attente
                      </Badge>
                      <Badge className={`${
                        offre.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                        offre.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {offre.type_contrat}
                      </Badge>
                      {getRoleBadge(offre.created_by_role)}
                    </div>
                    
                    {/* Titre */}
                    <h3 className="font-bold text-lg text-slate-900 mb-1">{offre.intitule_poste}</h3>
                    
                    {/* Entreprise */}
                    {offre.entreprise_nom && (
                      <p className="text-slate-600 flex items-center gap-1 mb-2 text-sm">
                        <Building2 className="w-4 h-4" />
                        {offre.entreprise_nom}
                      </p>
                    )}
                    
                    {/* Infos */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {offre.lieu_mission} ({offre.zone_geographique})
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {offre.created_by_nom}
                      </span>
                    </div>
                    
                    {/* Date de soumission */}
                    <p className="text-xs text-slate-400">
                      Soumis le {new Date(offre.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setSelectedOffre(offre); setShowDetailDialog(true); }}
                    className="rounded-xl flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir détails
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => handleValider(offre.id)}
                    disabled={processing}
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 flex-1"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Valider
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => openRefusDialog(offre)}
                    disabled={processing}
                    className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Détail Offre */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          {selectedOffre && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-amber-100 text-amber-700">
                    <Clock className="w-3 h-3 mr-1" />
                    En attente de validation
                  </Badge>
                  {getRoleBadge(selectedOffre.created_by_role)}
                </div>
                <DialogTitle className="text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {selectedOffre.intitule_poste}
                </DialogTitle>
                <DialogDescription>
                  Soumis par {selectedOffre.created_by_nom} le {new Date(selectedOffre.created_at).toLocaleDateString('fr-FR')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Infos principales */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
                  <div>
                    <Label className="text-slate-500 text-sm">Type de contrat</Label>
                    <p className="font-medium">{selectedOffre.type_contrat}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Secteur</Label>
                    <p className="font-medium">{selectedOffre.secteur_activite}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Lieu</Label>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      {selectedOffre.lieu_mission} ({selectedOffre.zone_geographique})
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-sm">Entreprise</Label>
                    <p className="font-medium">{selectedOffre.entreprise_nom || 'Non spécifiée'}</p>
                  </div>
                </div>

                {/* Description */}
                {selectedOffre.description && (
                  <div>
                    <Label className="text-slate-700 font-semibold">Description</Label>
                    <div className="mt-2 p-4 bg-white border rounded-xl whitespace-pre-wrap text-sm">
                      {selectedOffre.description}
                    </div>
                  </div>
                )}

                {/* Compétences */}
                {selectedOffre.competences_requises && (
                  <div>
                    <Label className="text-slate-700 font-semibold">Compétences requises</Label>
                    <div className="mt-2 p-4 bg-white border rounded-xl whitespace-pre-wrap text-sm">
                      {selectedOffre.competences_requises}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-xl">
                  Fermer
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => openRefusDialog(selectedOffre)}
                  disabled={processing}
                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Refuser
                </Button>
                <Button 
                  onClick={() => handleValider(selectedOffre.id)}
                  disabled={processing}
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Valider et publier
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Refus */}
      <Dialog open={showRefusDialog} onOpenChange={setShowRefusDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Refuser l'offre
            </DialogTitle>
            <DialogDescription>
              {selectedOffre?.intitule_poste}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Motif du refus (optionnel)</Label>
            <Textarea
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
              placeholder="Indiquez la raison du refus (sera visible par le créateur)"
              className="mt-2 rounded-xl"
              rows={3}
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRefusDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleRefuser}
              disabled={processing}
              className="rounded-xl bg-red-600 hover:bg-red-700"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirmer le refus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OffresEnAttenteAdmin;
